import { ChangeDetectionStrategy, Component, Input, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslationService } from '@/layout/service/translation.service';
import { CloudApiService, DeviceIssue } from '@/cloud/services/cloud-api.service';
import { EmptyStateComponent } from '@/shared/ui/empty-state/empty-state.component';
import { LoadingSkeletonComponent } from '@/shared/ui/loading-skeleton/loading-skeleton.component';

@Component({
    selector: 'app-hotlist',
    standalone: true,
    imports: [CommonModule, RouterModule, EmptyStateComponent, LoadingSkeletonComponent],
    templateUrl: './hotlist.component.html',
    styleUrl: './hotlist.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HotlistComponent {
    private api = inject(CloudApiService);
    private translation = inject(TranslationService);
    readonly t = this.translation.t;
    readonly expanded = signal(false);
    private requestId = 0;

    /** Namespace ID to filter issues */
    @Input() set namespaceId(value: string | null) {
        this._namespaceId.set(value);
    }

    /** Location ID to filter issues (optional) */
    @Input() set locationId(value: string | null) {
        this._locationId.set(value);
    }

    /** Max number of issues to display before truncation */
    @Input() maxItems = 10;

    private _namespaceId = signal<string | null>(null);
    private _locationId = signal<string | null>(null);

    issues = signal<DeviceIssue[]>([]);
    loading = signal(false);
    error = signal<string | null>(null);

    private loadEffect = effect((onCleanup) => {
        const nsId = this._namespaceId();
        const locId = this._locationId();
        const request = ++this.requestId;
        onCleanup(() => this.requestId++);
        this.issues.set([]);
        this.error.set(null);
        this.expanded.set(false);
        if (nsId) {
            void this.loadIssues(nsId, locId ?? undefined, request);
        } else {
            this.loading.set(false);
        }
    });

    private async loadIssues(namespaceId: string, locationId: string | undefined, request: number): Promise<void> {
        this.loading.set(true);
        this.error.set(null);

        try {
            const result = await this.api.getDeviceIssues(namespaceId, locationId);
            if (request !== this.requestId) return;
            // Sort: offline devices first, then errors, then warnings, then by time
            const sorted = [...result].sort((a, b) => {
                // Offline devices always come first
                const offlineCompare = (b.isOffline ? 1 : 0) - (a.isOffline ? 1 : 0);
                if (offlineCompare !== 0) return offlineCompare;

                // Then errors before warnings
                if (a.level !== b.level) {
                    return a.level === 'Error' ? -1 : 1;
                }

                // Finally sort by time (newest first)
                const timeA = a.time || '';
                const timeB = b.time || '';
                return timeB.localeCompare(timeA);
            });
            this.issues.set(sorted);
        } catch (err: unknown) {
            if (request !== this.requestId) return;
            console.error('Failed to load device issues', err);
            const detail = err && typeof err === 'object' && 'error' in err ? err.error : undefined;
            this.error.set(typeof detail === 'string' ? detail : err instanceof Error ? err.message : this.t()('hotlist.loadError'));
        } finally {
            if (request === this.requestId) this.loading.set(false);
        }
    }

    /** Limited issues for display */
    get displayedIssues(): DeviceIssue[] {
        return this.expanded() ? this.issues() : this.issues().slice(0, this.maxItems);
    }

    /** Whether there are more issues than displayed */
    get hasMore(): boolean {
        return this.issues().length > this.maxItems;
    }

    /** Count of hidden issues */
    get hiddenCount(): number {
        return this.issues().length - this.displayedIssues.length;
    }

    /** Total issue count */
    get totalCount(): number {
        return this.issues().length;
    }

    get offlineCount(): number {
        return this.issues().filter((issue) => issue.isOffline).length;
    }

    get errorCount(): number {
        return this.issues().filter((issue) => !issue.isOffline && this.isError(issue)).length;
    }

    get warningCount(): number {
        return this.issues().filter((issue) => !issue.isOffline && !this.isError(issue)).length;
    }

    formatTime(value: string): string {
        const date = new Date(value);
        return Number.isFinite(date.getTime()) ? date.toLocaleString(this.translation.currentLanguage(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
    }

    isError(issue: DeviceIssue): boolean {
        return issue.level === 'Error';
    }

    /**
     * A cloud-side storage problem rather than a fault the device reported — the device is
     * checking in normally, so nothing it says would ever flag it.
     */
    isCheckInHealth(issue: DeviceIssue): boolean {
        return issue.source === 'checkin-health';
    }
}
